import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";

const BYTES_PER_ROW = 16;

/** Printable ASCII is 32-126; rest show as '.' */
function byteToAscii(b: number): string {
  return b >= 32 && b <= 126 ? String.fromCharCode(b) : ".";
}

/** One line per 16 bytes: printable ASCII or '.' */
function bytesToAsciiPreview(bytes: number[]): string {
  const lines: string[] = [];
  for (let i = 0; i < bytes.length; i += BYTES_PER_ROW) {
    const row = bytes.slice(i, i + BYTES_PER_ROW);
    lines.push(row.map(byteToAscii).join(""));
  }
  if (lines.length === 0) lines.push("");
  return lines.join("\n");
}

function stringToBytes(s: string): number[] {
  return Array.from(new TextEncoder().encode(s));
}

function bytesToString(bytes: number[]): string {
  return new TextDecoder().decode(new Uint8Array(bytes));
}

function byteToHex(b: number): string {
  return b.toString(16).toUpperCase().padStart(2, "0");
}

/** Format bytes as hex dump lines: "0000 48 65 6C 6C 6F ..." */
function bytesToHexDump(bytes: number[]): string {
  const lines: string[] = [];
  for (let i = 0; i < bytes.length; i += BYTES_PER_ROW) {
    const offset = i.toString(16).toUpperCase().padStart(4, "0");
    const row = bytes.slice(i, i + BYTES_PER_ROW);
    const hex = row.map(byteToHex).join(" ");
    lines.push(`${offset} ${hex}`);
  }
  if (lines.length === 0) lines.push("0000 ");
  return lines.join("\n");
}

/** Parse hex dump: first token per line is 4-char offset; remaining tokens are 2-char hex bytes. */
function parseHexDump(text: string): number[] {
  const bytes: number[] = [];
  const lines = text.split(/\r?\n/);
  for (const line of lines) {
    const tokens = line.trim().split(/\s+/).filter(Boolean);
    for (let i = 0; i < tokens.length; i++) {
      const t = tokens[i];
      const isOffset = t.length === 4 && /^[0-9A-Fa-f]{4}$/.test(t) && i === 0;
      if (isOffset) continue;
      if (t.length === 2 && /^[0-9A-Fa-f]{2}$/.test(t)) {
        bytes.push(parseInt(t, 16));
      }
    }
  }
  return bytes;
}

export default function HexEditor(props: {
  value: string;
  onChange: (value: string) => void;
  onClose: () => void;
}) {
  const [hexText, setHexText] = useState(() =>
    bytesToHexDump(stringToBytes(props.value))
  );

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const previewRef = useRef<HTMLPreElement>(null);

  useEffect(() => {
    setHexText(bytesToHexDump(stringToBytes(props.value)));
  }, [props.value]);

  const asciiPreview = useMemo(() => {
    try {
      const bytes = parseHexDump(hexText);
      return bytesToAsciiPreview(bytes);
    } catch {
      return "";
    }
  }, [hexText]);

  const handleHexScroll = useCallback(() => {
    const ta = textareaRef.current;
    const pre = previewRef.current;
    if (ta && pre && ta.scrollHeight > ta.clientHeight) {
      const ratio = ta.scrollTop / (ta.scrollHeight - ta.clientHeight) || 0;
      pre.scrollTop = ratio * (pre.scrollHeight - pre.clientHeight);
    }
  }, []);

  const handlePreviewScroll = useCallback(() => {
    const ta = textareaRef.current;
    const pre = previewRef.current;
    if (ta && pre && pre.scrollHeight > pre.clientHeight) {
      const ratio = pre.scrollTop / (pre.scrollHeight - pre.clientHeight) || 0;
      ta.scrollTop = ratio * (ta.scrollHeight - ta.clientHeight);
    }
  }, []);

  const handleApply = useCallback(() => {
    try {
      const bytes = parseHexDump(hexText);
      props.onChange(bytesToString(bytes));
      props.onClose();
    } catch {
      // If parse fails, don't close - user can fix
    }
  }, [hexText, props]);

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6">Hex editor</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" onClick={handleApply}>
            Apply
          </Button>
          <Button variant="outlined" onClick={props.onClose}>
            Close
          </Button>
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Edit hex bytes (2 hex digits per byte). First column is offset; each line has up to 16 bytes. Right: ASCII preview (non-printable as .).
      </Typography>
      <Box sx={{ display: "flex", gap: 1, alignItems: "stretch", minHeight: "60vh", maxHeight: "65vh" }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            "& textarea": {
              display: "block",
              width: "100%",
              height: "100%",
              minHeight: "60vh",
              maxHeight: "65vh",
              fontFamily: "monospace",
              fontSize: "0.85rem",
              lineHeight: 1.4,
              p: 1.5,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              bgcolor: "grey.100",
              resize: "none",
              boxSizing: "border-box",
            },
          }}
        >
          <textarea
            ref={textareaRef}
            value={hexText}
            onChange={(e) => setHexText(e.target.value)}
            onScroll={handleHexScroll}
            spellCheck={false}
          />
        </Box>
        <Box
          component="pre"
          ref={previewRef}
          onScroll={handlePreviewScroll}
          sx={{
            width: 280,
            flexShrink: 0,
            minHeight: "60vh",
            maxHeight: "65vh",
            overflow: "auto",
            margin: 0,
            fontFamily: "monospace",
            fontSize: "0.85rem",
            lineHeight: 1.4,
            p: 1.5,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            bgcolor: "grey.200",
            whiteSpace: "pre",
          }}
        >
          {asciiPreview}
        </Box>
      </Box>
    </Paper>
  );
}
